import { allocate, lineTotal, mulDiv, percentOf, vatFromInclusive } from './money.js';

/**
 * The pricing engine. The till uses it to show totals as items are scanned; the API runs
 * the same function on the server and only trusts its own result. All amounts are integer
 * cents, prices are VAT-inclusive (normal Zimbabwean retail practice).
 *
 * Order of application on each line:
 *   1. gross = unit price × quantity
 *   2. best applicable automatic promotion
 *   3. manual line discount (cashier, needs permission)
 *   4. sale-level discount, shared across lines in proportion to their value
 *   5. VAT extracted from the final line amount
 */

export type Discount = { kind: 'percent'; bp: number } | { kind: 'amount'; cents: number };

export interface PromotionRule {
  id: string;
  name: string;
  kind: 'percent_off' | 'fixed_price' | 'buy_x_get_y';
  productIds: string[];
  categoryIds: string[];
  percentBp?: number | null;
  fixedPriceCents?: number | null;
  buyQty?: number | null;
  getQty?: number | null;
}

export interface CartLineInput {
  key: string;
  productId: string;
  categoryId: string | null;
  qtyMilli: number;
  unitPriceCents: number;
  vatRateBp: number;
  /** True when quantities are whole units (needed for buy-X-get-Y). */
  wholeUnits: boolean;
  manualDiscount?: Discount | null;
}

export interface PricedLine {
  key: string;
  grossCents: number;
  promotionId: string | null;
  promotionName: string | null;
  promoDiscountCents: number;
  manualDiscountCents: number;
  saleDiscountCents: number;
  netCents: number;
  vatRateBp: number;
  vatCents: number;
}

export interface PricedCart {
  lines: PricedLine[];
  grossCents: number;
  promoDiscountCents: number;
  manualDiscountCents: number;
  saleDiscountCents: number;
  discountCents: number;
  totalCents: number;
  vatCents: number;
  vatBreakdown: { rateBp: number; totalCents: number; vatCents: number }[];
}

function applyDiscount(base: number, d: Discount | null | undefined): number {
  if (!d || base <= 0) return 0;
  const raw = d.kind === 'percent' ? percentOf(base, Math.min(Math.max(d.bp, 0), 10_000)) : Math.max(d.cents, 0);
  return Math.min(raw, base);
}

export function promotionDiscount(line: CartLineInput, gross: number, promo: PromotionRule): number {
  const applies = promo.productIds.includes(line.productId) || (!!line.categoryId && promo.categoryIds.includes(line.categoryId));
  if (!applies) return 0;
  switch (promo.kind) {
    case 'percent_off':
      return Math.min(percentOf(gross, promo.percentBp ?? 0), gross);
    case 'fixed_price': {
      const promoTotal = lineTotal(promo.fixedPriceCents ?? line.unitPriceCents, line.qtyMilli);
      return Math.max(0, gross - promoTotal);
    }
    case 'buy_x_get_y': {
      const x = promo.buyQty ?? 0;
      const y = promo.getQty ?? 0;
      if (!line.wholeUnits || x <= 0 || y <= 0) return 0;
      const units = Math.floor(line.qtyMilli / 1000);
      const free = Math.floor(units / (x + y)) * y;
      return Math.min(lineTotal(line.unitPriceCents, free * 1000), gross);
    }
  }
}

export function priceCart(
  lines: CartLineInput[],
  opts: { saleDiscount?: Discount | null; promotions?: PromotionRule[] } = {},
): PricedCart {
  const promotions = opts.promotions ?? [];
  const stage = lines.map((l) => {
    const gross = lineTotal(l.unitPriceCents, l.qtyMilli);
    let best: { promo: PromotionRule; amount: number } | null = null;
    for (const p of promotions) {
      const amount = promotionDiscount(l, gross, p);
      if (amount > 0 && (!best || amount > best.amount)) best = { promo: p, amount };
    }
    const promo = best?.amount ?? 0;
    const manual = applyDiscount(gross - promo, l.manualDiscount);
    return { l, gross, best, promo, manual, afterLine: gross - promo - manual };
  });

  const afterLinesTotal = stage.reduce((a, s) => a + s.afterLine, 0);
  const saleDiscount = applyDiscount(afterLinesTotal, opts.saleDiscount);
  const shares = saleDiscount > 0 ? allocate(saleDiscount, stage.map((s) => s.afterLine)) : stage.map(() => 0);

  const priced: PricedLine[] = stage.map((s, i) => {
    const net = s.afterLine - shares[i]!;
    return {
      key: s.l.key,
      grossCents: s.gross,
      promotionId: s.best?.promo.id ?? null,
      promotionName: s.best?.promo.name ?? null,
      promoDiscountCents: s.promo,
      manualDiscountCents: s.manual,
      saleDiscountCents: shares[i]!,
      netCents: net,
      vatRateBp: s.l.vatRateBp,
      vatCents: vatFromInclusive(net, s.l.vatRateBp),
    };
  });

  const sum = (f: (l: PricedLine) => number) => priced.reduce((a, l) => a + f(l), 0);
  const byRate = new Map<number, { totalCents: number; vatCents: number }>();
  for (const l of priced) {
    const r = byRate.get(l.vatRateBp) ?? { totalCents: 0, vatCents: 0 };
    r.totalCents += l.netCents;
    byRate.set(l.vatRateBp, r);
  }
  // VAT is extracted once per rate from the total at that rate (ZIMRA's FDMS formula), so the
  // invoice and the fiscal receipt agree to the cent. Line VAT is indicative only.
  for (const [rateBp, r] of byRate) r.vatCents = vatFromInclusive(r.totalCents, rateBp);
  const promoD = sum((l) => l.promoDiscountCents);
  const manualD = sum((l) => l.manualDiscountCents);
  const saleD = sum((l) => l.saleDiscountCents);
  return {
    lines: priced,
    grossCents: sum((l) => l.grossCents),
    promoDiscountCents: promoD,
    manualDiscountCents: manualD,
    saleDiscountCents: saleD,
    discountCents: promoD + manualD + saleD,
    totalCents: sum((l) => l.netCents),
    vatCents: [...byRate.values()].reduce((a, r) => a + r.vatCents, 0),
    vatBreakdown: [...byRate.entries()].sort((a, b) => b[0] - a[0]).map(([rateBp, v]) => ({ rateBp, ...v })),
  };
}

/**
 * Share of a line amount for part of its quantity. Computed as the difference of cumulative
 * shares, so any sequence of partial returns adds up exactly to the original amount.
 */
export function proRata(amount: number, totalQtyMilli: number, alreadyMilli: number, thisMilli: number): number {
  if (totalQtyMilli <= 0) return 0;
  return mulDiv(amount, alreadyMilli + thisMilli, totalQtyMilli) - mulDiv(amount, alreadyMilli, totalQtyMilli);
}
